import { useState, useEffect, RefObject, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Base64 } from 'js-base64';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Sticky } from 'react-sticky';
import cn from 'classnames';

import { IAnalyticsData } from 'APIPackage/AnalyticsModule/interfaces/IAnalyticsData';
import { ROUTE_ANALYTICS_KNM } from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { TableWithExternalScroll } from 'UIPackage/MainModule/components/TableWithExternalScroll';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { getAnnounceHeight } from 'CorePackage/MainModule/reducers/coreReducer';

import styles from './AnalyticsTable.module.scss';
import { initialAnalyticsTableColumns } from '../constants/initialAnalyticsTableColumns';
import { IAnalyticsFilter } from '../interfaces/IAnalyticsFilter';
import { AnalyticsAdminLevelsEnum } from '../enums/AnalyticsAdminLevelsEnum';

interface IAnalyticsTableProps {
  data: IAnalyticsData[];
  filter: IAnalyticsFilter;
  announceHeight?: number;
}

interface ITableProps {
  innerRef?: RefObject<HTMLTableElement>;
}

const PAGE_SIZE = 50;

const AnalyticsTableWithoutStore = ({ data, filter, announceHeight }: IAnalyticsTableProps) => {
  const [currentData, setCurrentData] = useState<Array<IAnalyticsData>>([]);
  const [hasMore, setHasMore] = useState(false);

  const getMoreData = useCallback(() => {
    if (currentData.length === data.length) {
      setHasMore(false);
    } else {
      setCurrentData(data.slice(0, currentData.length + PAGE_SIZE));
    }
  }, [currentData.length, data]);

  const getParams = (knmType: string, knoId: string | null, name: string) => {
    const params = new URLSearchParams();
    let nameAdminLevel: AnalyticsAdminLevelsEnum | undefined;

    params.set('knmType', knmType);
    if (knoId !== null) {
      params.set('knoId', knoId);
    }
    switch (name) {
      case 'Федеральный уровень':
        nameAdminLevel = AnalyticsAdminLevelsEnum.FEDERAL;
        break;
      case 'Региональный уровень':
        nameAdminLevel = AnalyticsAdminLevelsEnum.REGION;
        break;
      case 'Муниципальный уровень':
        nameAdminLevel = AnalyticsAdminLevelsEnum.MUNICIPAL;
        break;
      case 'Уровень не указан':
        nameAdminLevel = AnalyticsAdminLevelsEnum.NOTSPECIFIED;
        break;
      default:
    }
    if (nameAdminLevel !== undefined) {
      params.set('nameAdminLevel', nameAdminLevel);
    }
    params.set('filter', Base64.encode(JSON.stringify(filter), true));

    return `${params}`;
  };

  useEffect(() => {
    setCurrentData(data.slice(0, PAGE_SIZE));
    setHasMore(data.length > PAGE_SIZE);
  }, [data]);

  const handleMainTableScroll = (innerRef?: RefObject<HTMLTableElement>) => () => {
    const thead = innerRef?.current?.querySelector('thead');

    if (thead) {
      thead.scrollLeft = innerRef?.current?.scrollLeft ?? 0;
    }
  };

  const Table = ({ innerRef }: ITableProps) => (
    <table ref={innerRef} className={styles.Table} onScroll={handleMainTableScroll(innerRef)}>
      <InfiniteScroll
        dataLength={currentData.length}
        next={getMoreData}
        hasMore={hasMore}
        className={styles.InfiniteScroll}
        loader
      >
        <Sticky disableCompensation disableHardwareAcceleration>
          {({ style, isSticky, wasSticky }) => {
            if (isSticky && !wasSticky) {
              // если не использовать setTimeout, thead.scrollLeft при смене isSticky не изменится
              setTimeout(() => {
                handleMainTableScroll(innerRef)();
              });
            }

            return (
              <thead
                style={{
                  ...deleteObjectProperties(style, ['top', 'left']),
                  top: (announceHeight ?? 0) + 70,
                }}
                className={cn({ [styles.TheadSticky]: isSticky })}
              >
                <tr>
                  {initialAnalyticsTableColumns.map(column => {
                    const theadAttributes = {
                      ...(column.columnType === 'planned' && column.startSection === true && { 'data-start': true }),
                      ...(column.columnType === 'notplanned' &&
                        column.startSection !== true && { 'data-not-planned': true }),
                      ...(column.columnType === 'notplanned' &&
                        column.startSection !== undefined && { 'data-not-planned-start': true }),
                      ...(column.columnType === 'violations' && { 'data-violations': true }),
                      ...(column.startSection === true && { 'data-start-section': true }),
                    };

                    return (
                      <th key={column.id} className={styles.TheadCell} {...theadAttributes}>
                        {column.combinedText === true ? (
                          <>
                            <span style={{ fontWeight: 800, fontSize: '1rem' }}>Из них:</span>
                            <br />
                            {column.name}
                          </>
                        ) : (
                          column.name
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            );
          }}
        </Sticky>
        <tbody>
          {currentData.map((item, index) => {
            return (
              <tr key={item.id ?? index} className={styles.TbodyRow}>
                {initialAnalyticsTableColumns.map(column => {
                  const tbodyAttributes = {
                    ...(column.columnType === 'knoName' && { 'data-kno': true }),
                    ...(column.columnType === 'planned' &&
                      column.startSection !== undefined && { 'data-planned': true }),
                    ...(column.columnType === 'notplanned' &&
                      column.startSection !== undefined && { 'data-not-planned': true }),
                    ...(column.startSection === true && { 'data-start-section': true }),
                    ...(column.columnType === 'violations' && { 'data-violations': true }),
                  };

                  return (
                    <td key={column.id + item.id} className={styles.TbodyCell} {...tbodyAttributes}>
                      <div className={styles.Export}>
                        {column.id !== 'name' ? (
                          <Link
                            className={styles.ExportButton}
                            to={`${ROUTE_ANALYTICS_KNM}?${getParams(column.id, item.id, item.name)}`}
                          >
                            {item[column.id]}
                          </Link>
                        ) : (
                          item[column.id]
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </InfiniteScroll>
    </table>
  );

  const TableWithScroll = TableWithExternalScroll(Table);

  return (
    <div className={styles.Container}>
      <div className={styles.Panel}>
        <div className={styles.PanelBody}>
          <TableWithScroll />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  data: state.analytics.data,
  filter: state.analytics.filter,
  announceHeight: getAnnounceHeight(state),
});

export const AnalyticsTable = connect(mapStateToProps)(AnalyticsTableWithoutStore);
